import React, { useRef } from "react";
import { useScroll, useTransform, motion, MotionValue } from "framer-motion";
import { FaUtensils } from "react-icons/fa";
import { Stat } from "./stat";
import { CompontHeader } from "./compont-header";
import { calculatePastYears } from "../utils/calculate-past-years";

export const AboutUs = () => {
  return (
    <div className="relative mx-auto grid h-full w-full max-w-7xl grid-cols-1 gap-8 px-4 md:grid-cols-3">
      <Copy />
      <Carousel />
    </div>
  );
};

const Copy = () => {
  return (
    <div className="col-span-2 flex h-fit w-full flex-col justify-center gap-4 py-12 md:sticky md:top-0 md:h-screen xl:h-1/2">
      <CompontHeader text="über uns" />
      <h2 className="mb-4 mt-2 text-3xl font-medium leading-tight">
        Willkommen bei&nbsp;
        <span className="text-[#FAED04] inline-flex">
          <FaUtensils />
        </span>
        &nbsp;Schmeckt&nbsp;Lecker
      </h2>
      <p className="text-lg">
        Ihrem traditionellen türkischen Schnellimbiss in Meschede-Freienohl!
        Seit {calculatePastYears()} Jahren sind wir eine ein beliebter
        Treffpunkt für Einheimische, die echten Geschmack und herzliche
        Gastfreundschaft schätzen. Unser Imbiss ist seit Gründung ein Symbol für
        Qualität und Tradition. Wir sind stolz darauf, ein Familienbetrieb zu
        sein, der in der Gemeinde tief verwurzelt ist.
      </p>
      <p className="text-lg">
        Jeden Tag wählen wir sorgfältig frische Zutaten aus, um sicherzustellen,
        dass unsere Speisen immer von höchster Qualität sind. Wir glauben, dass
        gutes Essen mit Liebe und den besten Zutaten beginnt. Im Laufe der Jahre
        haben wir viele Herausforderungen gemeistert, darunter die Ankunft
        etlicher neuer Mitbewerber. Doch dank unserer treuen Kunden haben wir
        weiterhin Erfolg. Wir sind dankbar für die anhaltende Unterstützung und
        das Vertrauen, das uns die Gemeinschaft entgegenbringt.
      </p>
      <p className="text-lg">
        Wir freuen uns darauf, Sie bald zu begrüßen und mit Ihnen die reiche
        Tradition und den köstlichen Geschmack der türkischen Küche zu teilen.
        Kommen Sie vorbei und erleben Sie, warum wir seit über drei Jahrzehnten
        ein Favorit in Freienohl sind! Herzlich willkommen bei uns!
      </p>
      <div className="flex gap-4">
        <div className="rounded min-h-16 bg-[#FAED04] h-full w-[1px]" />
        <Stat
          num={calculatePastYears()}
          suffix="Jahre"
          subheading="Tradition"
        />
      </div>
    </div>
  );
};

const Carousel = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  return (
    <div className="relative w-full">
      <div ref={ref} className="relative z-0 flex flex-col gap-6 md:gap-12">
        <CarouselItem
          scrollYProgress={scrollYProgress}
          position={1}
          numItems={3}
          src="/sl_spies.jpeg"
        />
        <CarouselItem
          scrollYProgress={scrollYProgress}
          position={2}
          numItems={3}
          src="/doenerteller_gros.jpeg"
        />
        <CarouselItem
          scrollYProgress={scrollYProgress}
          position={3}
          numItems={3}
          src="/sl_theke2.jpeg"
        />
      </div>

      <Buffer />
    </div>
  );
};

const CarouselItem = ({
  scrollYProgress,
  position,
  numItems,
  src,
}: {
  scrollYProgress: MotionValue<number>;
  position: number;
  numItems: number;
  src: string;
}) => {
  const stepSize = 1 / numItems;
  const end = stepSize * position;
  const start = end - stepSize;

  const opacity = useTransform(scrollYProgress, [start, end], [1, 0]);
  const scale = useTransform(scrollYProgress, [start, end], [1, 0.75]);

  return (
    <motion.div
      style={{
        opacity,
        scale,
      }}
      className="grid w-full shrink-0 place-content-center rounded-2xl bg-neutral-900"
    >
      <div className="relative w-full h-80">
        <img
          src={src}
          alt={`Feature ${position}`}
          className="w-full h-full object-cover rounded-2xl"
        />
      </div>
    </motion.div>
  );
};

const Buffer = () => <div className="h-12 w-full md:h-24" />;
